import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Building2, UserRound } from 'lucide-react';
import { Drawer, Field, SearchBox, Select, useDebounced } from '@/components/live';
import { createRow } from '@/api/resources';
import { toast } from '@/lib/toast';

/**
 * Put an entity that is already on the register into a group (owner, 2026-09-26: "Add button shows
 * list of entity? where we can select from already registered entities based on the grouping").
 *
 * WHAT ADDING ACTUALLY DOES. It grants a ROLE. An entity is not "in" the processing group by being
 * filed there - it is in it because it holds the `processor` role from a date. That is why this
 * picks an existing party rather than creating one: a sawmill the organisation already deals with
 * is already a party, and a second record for it would split its KYC, its consents and its history
 * down the middle.
 *
 * WHERE A GROUP HAS TWO ROLES the reader is asked which, rather than one being guessed. Inputs
 * covers nurseries and material suppliers, and they are not interchangeable: a nursery's licence
 * and a supplier's are different documents.
 *
 * ENTITIES ALREADY HOLDING THE ROLE ARE HIDDEN, because granting it twice is a duplicate rather
 * than a second engagement, and the database refuses it anyway - better to not offer it than to
 * offer it and fail.
 */

const GROUP_ROLES: Record<string, Array<{ id: string; label: string }>> = {
  inputs: [
    { id: 'nursery', label: 'Nursery - raises seedlings' },
    { id: 'supplier', label: 'Supplier - provides materials' },
  ],
  services: [
    { id: 'contractor', label: 'Contractor - supplies labour' },
    { id: 'service_provider', label: 'Service provider - machinery, irrigation, advice' },
  ],
  plantation: [
    { id: 'farmer', label: 'Farmer - grows the timber' },
  ],
  intermediaries: [
    { id: 'transporter', label: 'Transporter - carries material' },
    { id: 'aggregator', label: 'Agent or aggregator - buys to resell' },
  ],
  processing: [
    { id: 'processor', label: 'Processor - saws, seasons or treats' },
  ],
  buyers: [
    { id: 'buyer', label: 'Buyer - takes the output' },
    { id: 'exporter', label: 'Exporter - sells onward abroad' },
  ],
};

const STAGE_LABEL: Record<string, string> = {
  inputs: 'Inputs', services: 'Services', plantation: 'Plantation',
  intermediaries: 'Intermediaries', processing: 'Processing', buyers: 'Buyers',
};

export interface Candidate {
  id: string;
  display_name: string;
  legal_name: string | null;
  party_code: string | null;
  party_category: string;
  geography_name: string | null;
  role_types: string[] | null;
}

export function AddEntityToGroup({
  group, candidates, onClose,
}: {
  /** The group whose add button was pressed, or null when shut. */
  group: string | null;
  candidates: Candidate[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [roleType, setRoleType] = useState('');
  const [partyId, setPartyId] = useState('');
  const [search, setSearch] = useState('');
  const term = useDebounced(search).trim().toLowerCase();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  const roles = group ? GROUP_ROLES[group] ?? [] : [];

  useEffect(() => {
    if (!group) return;
    // One role in the group means there is nothing to ask; more than one is a real question.
    setRoleType(roles.length === 1 ? roles[0]!.id : '');
    setPartyId('');
    setSearch('');
    setError(undefined);
  }, [group]);

  const shown = useMemo(() => candidates
    // Already holds the role being granted: offering it would only produce a refusal.
    .filter((c) => !roleType || !(c.role_types ?? []).includes(roleType))
    .filter((c) => !term
      || c.display_name.toLowerCase().includes(term)
      || (c.legal_name ?? '').toLowerCase().includes(term)
      || (c.party_code ?? '').toLowerCase().includes(term))
    .slice(0, 50),
  [candidates, roleType, term]);

  async function save() {
    if (!partyId || !roleType) return;
    setError(undefined);
    setSaving(true);
    try {
      await createRow('party_roles', {
        party_id: partyId,
        role_type: roleType,
        effective_from: new Date().toISOString().slice(0, 10),
        status: 'active',
      });
      // Both the list and the chart counts move when a role is granted.
      await queryClient.invalidateQueries({ queryKey: ['ecosystem_entities'] });
      await queryClient.invalidateQueries({ queryKey: ['ecosystem-groups'] });
      await queryClient.invalidateQueries({ queryKey: ['ecosystem-summary'] });
      await queryClient.invalidateQueries({ queryKey: ['party_roles'] });
      toast.success('Added to the group.');
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer
      footer={(
        <>
          <button className="cancel" onClick={onClose} type="button">Cancel</button>
          <button
            className="primary"
            disabled={saving || !partyId || !roleType}
            onClick={() => void save()}
            type="button"
          >
            {saving ? 'Adding...' : 'Add to group'}
          </button>
        </>
      )}
      onClose={onClose}
      open={Boolean(group)}
      subtitle="Choose someone already on the register. Adding grants them the role that puts them in this group."
      title={`Add to ${group ? STAGE_LABEL[group] ?? group : ''}`}
    >
      {roles.length > 1 && (
        <Field hint="A group can hold more than one kind of actor, and their papers differ." label="Acts as" required>
          <Select onChange={setRoleType} options={roles} placeholder="Choose a role" value={roleType} />
        </Field>
      )}

      <div className="toolbar-row">
        <SearchBox onChange={setSearch} placeholder="Name or code" value={search} />
      </div>

      {shown.length === 0 ? (
        <p className="state-note">
          {candidates.length === 0
            ? 'Nobody is on the register yet. Add the company on the party register first, then it can be given a role here.'
            : 'Nobody matches, or everyone who does already holds this role.'}
        </p>
      ) : (
        <ul className="pick-list" role="listbox">
          {shown.map((candidate) => (
            <li key={candidate.id}>
              <button
                aria-selected={partyId === candidate.id}
                className={partyId === candidate.id ? 'is-chosen' : undefined}
                onClick={() => setPartyId(candidate.id)}
                role="option"
                type="button"
              >
                {candidate.party_category === 'individual'
                  ? <UserRound size={15} /> : <Building2 size={15} />}
                <span>
                  <strong>{candidate.display_name}</strong>
                  <small>
                    {[candidate.party_code, candidate.geography_name,
                      (candidate.role_types ?? []).join(', ') || 'no role yet']
                      .filter(Boolean).join(' · ')}
                  </small>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {error && <p className="field-error" role="alert">{error}</p>}
    </Drawer>
  );
}
